import * as queryString from 'query-string';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import cookie from 'react-cookies';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { OriginArea } from '@videoblocks/events-ts/lib/storyblocks/search/OriginAreaEnum';
import { Telemetry } from '@videoblocks/kafka-rest-client';

import events from '../../../../Events/Events';
import StockItemHoverEventFactory from '../../../../Events/StockItemHoverEventFactory';
import { STORYBOARD_REQUEST_ID_COOKIE } from '../../../../Storyboards/constants';
import {
  selectHasVideoSubscription,
  selectIsLoggedIn,
  selectIsMobile,
  selectPrimarySubscription,
  selectUseNewDownloadOverlay,
} from '../../../../auth/AuthSelectors';
import { contentTypes } from '../../../../common/Constants';
import Logger from '../../../../common/Logger';
import SiteConstants from '../../../../common/SiteConstants/SiteConstants';
import LoadingIndicator from '../../../../common/components/LoadingIndicator';
import { StockDownloadContext } from '../../../../common/context/StockDownloadContext';
import { useMultiVideoPlayback } from '../../../../common/hooks/useMultiVideoPlayback';
import {
  StockItem,
  StockItemContext,
  StockItemFormat,
} from '../../../../common/types/StockItemTypes';
import {
  convertStockItemContextToOriginArea,
  getPreviewUrl,
  isMouseOverElement,
  sendBeacon,
} from '../../../../common/utils';
import {
  openVideoEditorModal,
  selectIsPolling,
  selectAIEditButtonVisible,
  selectShouldShowAIEditFeatures,
  selectVerticalVideoAIEditEnabled,
} from '../../../ContentEdit/ContentEditSlice';
import {
  DEFAULT_ASPECT_RATIO,
  VERTICAL_ASPECT_RATIO_THRESHOLD,
} from '../../../Layouts/constants';
import Video from '../../../LegacyStorywind/Video/Video';
import { DrawerType } from '../../SearchTypes';
import {
  collapseMoreLikeThisDrawer,
  expandMoreLikeThisDrawer,
  setSelectedStockItemOptions,
  updateSearchPage,
} from '../../actions/SearchActions';
import {
  selectDrawerIsOpen,
  selectDrawerSelectedItemId,
  selectDrawerType,
  selectSearchFeatures,
  selectSelectedStockItem,
} from '../../selectors/searchSelectors';
import StockItemIcons from './StockItemIcons';
import VideoStockItemDownloader from './VideoStockItemCard/DownloadOverlay/VideoStockItemDownloader';
import { VideoCardActionButtons } from './VideoStockItemCard/VideoCardActionButtons';
import { VideoCardOverlay } from './VideoStockItemCard/VideoCardOverlay';
import VideoStockItemFormatSelector from './VideoStockItemFormatSelector';

export type VideoStockItemCardProps = {
  className?: string;
  containPoppers?: boolean;
  context: StockItemContext;
  draggableCard?: boolean;
  hideOnThreeColumnLayout: boolean;
  isSelected: boolean;
  page: number;
  searchLogParameters?: object;
  shouldInvertControlsVisibility?: boolean;
  shouldShowDownloadButton?: boolean;
  shouldStockItemHaveNewFlag: boolean;
  shouldShowMakerButton?: boolean;
  stockItem: StockItem;
  stockItemCardContext?: { title: string; position: number };
  stockItemFormats: StockItemFormat[];
  style?: React.CSSProperties;
  thumbnailClassNames?: string;
  overlayGradientClassNames?: string;
  downloadButtonTelemetryAction?: () => void;
  makerButtonTelemetryAction?: () => void;
  favoriteButtonTelemetryAction?: () => void;
  openDetailsInNewWindow?: boolean;
  openFoldersInNewWindow?: boolean;
  redirectToSignUpLink: boolean;
  clickOriginArea?: OriginArea;
  isFocusOutlineVisible?: boolean;
  videoStyles?: React.CSSProperties;
  drawerPortalContainer?: HTMLElement;
};

const VideoStockItemCard = ({
  stockItem,
  style = {},
  draggableCard = false,
  page,
  stockItemCardContext,
  context,
  stockItemFormats,
  shouldInvertControlsVisibility = false,
  shouldShowDownloadButton = true,
  downloadButtonTelemetryAction = null,
  makerButtonTelemetryAction = null,
  favoriteButtonTelemetryAction = null,
  containPoppers = false,
  openFoldersInNewWindow = false,
  shouldStockItemHaveNewFlag,
  thumbnailClassNames = '',
  overlayGradientClassNames = '',
  shouldShowMakerButton: shouldShowMakerButtonProp = true,
  isSelected,
  drawerPortalContainer = null,
  className = '',
  hideOnThreeColumnLayout,
  redirectToSignUpLink,
  searchLogParameters = {},
  openDetailsInNewWindow = false,
  clickOriginArea,
  isFocusOutlineVisible,
  videoStyles,
}: VideoStockItemCardProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const relatedVideosButtonRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const stockDownloadContext = useContext(StockDownloadContext);

  // Redux hooks
  const dispatch = useDispatch();

  // Redux state selectors
  const selectedStockItem = useSelector(selectSelectedStockItem);
  const features = useSelector(selectSearchFeatures);
  const isStockItemSelected = selectedStockItem.id === stockItem.id;
  const isMobile = useSelector(selectIsMobile);
  const formatSelectorVisible = isStockItemSelected
    ? selectedStockItem.formatSelectorVisible
    : false;
  const selectedFormatName = isStockItemSelected
    ? selectedStockItem.selectedFormatName
    : null;
  const selectedFormatResolution = isStockItemSelected
    ? selectedStockItem.selectedFormatResolution
    : null;
  const drawerIsOpen = useSelector(selectDrawerIsOpen);
  const drawerSelectedItemId = useSelector(selectDrawerSelectedItemId);
  const drawerType = useSelector(selectDrawerType);
  const downloadPermissions = features.downloadPermissions;
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const videoSubscription = useSelector(selectHasVideoSubscription);
  const primarySubscription = useSelector(selectPrimarySubscription);
  const useNewDownloadOverlay = useSelector(selectUseNewDownloadOverlay);
  const allowedToSeeAIEditButton = useSelector(selectAIEditButtonVisible);
  const isContentEditPolling = useSelector(selectIsPolling);
  const shouldShowAIEditFeatures = useSelector(selectShouldShowAIEditFeatures);
  const verticalVideoAIEditEnabled = useSelector(
    selectVerticalVideoAIEditEnabled
  );
  const [accumulatedPlayMs, setAccumulatedPlayMs] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoggerExecuted, setIsLoggerExecuted] = useState(false);
  const [hoverStartTime, setHoverStartTime] = useState<number | null>(null);
  const [playStartTime, setPlayStartTime] = useState<number | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isDrawerHovering, setIsDrawerHovering] = useState(false);

  // Use Redux video playback management
  const { isPlaying, play, pause, reset } = useMultiVideoPlayback(
    stockItem.id,
    videoRef
  );
  // Touch drag/viewport listeners
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const handleTouchMove = () => {
      if (!isDragging) setIsDragging(true);
    };
    const handleTouchStart = () => {
      if (isDragging) setIsDragging(false);
    };

    node.addEventListener('touchmove', handleTouchMove);
    node.addEventListener('touchstart', handleTouchStart);

    return () => {
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchstart', handleTouchStart);
    };
    // eslint-disable-next-line
  }, []);

  const aspectRatio = stockItem?.aspectRatio || DEFAULT_ASPECT_RATIO;
  const isVertical =
    stockItem?.isVertical || aspectRatio <= VERTICAL_ASPECT_RATIO_THRESHOLD;

  const playDurationMs = useCallback(() => {
    return playStartTime ? Date.now() - playStartTime : 0;
  }, [playStartTime]);

  const getSelectedFormat = useCallback(() => {
    return stockItemFormats.length >= 2 && selectedFormatName
      ? stockItemFormats.find(
          (stockItemFormat) =>
            stockItemFormat.formatName === selectedFormatName &&
            stockItemFormat.resolution === selectedFormatResolution
        )
      : stockItemFormats[0];
  }, [stockItemFormats, selectedFormatName, selectedFormatResolution]);

  // Helper: is subscriptionless user
  const isASubscriptionlessUser = isLoggedIn && !videoSubscription;

  // Helper: get click origin area
  const getClickOriginArea = useCallback(() => {
    if (clickOriginArea) return clickOriginArea;
    switch (context) {
      case StockItemContext.SEARCH:
        return OriginArea.SEARCH;
      case StockItemContext.SIMILAR_CONTENT:
        return OriginArea.STOCK_DETAILS;
      case StockItemContext.DRAWER:
        if (drawerType === DrawerType.MORE_LIKE_THIS) {
          return OriginArea.MORE_LIKE_THIS;
        }
        break;
      default:
        return null;
    }
  }, [clickOriginArea, context, drawerType]);

  const shouldShowSimilarContent =
    stockItem.numCollapses > 2 && context === StockItemContext.SEARCH;

  const shouldShowMakerButton =
    shouldShowMakerButtonProp &&
    (stockItem.contentType === 'footage' ||
      stockItem.contentType === 'motionbackground') &&
    !stockItem.is360 &&
    !isMobile;

  const shouldShowAIEditButton =
    allowedToSeeAIEditButton &&
    shouldShowAIEditFeatures &&
    stockItem.contentType === contentTypes.FOOTAGE &&
    (!isVertical || verticalVideoAIEditEnabled);

  const getContainerClassNames = useCallback(() => {
    return twMerge(
      'stock-item stock-item-v2 h-full w-full relative',
      isFocusOutlineVisible &&
        'rounded-2xl focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2',
      className,
      isSelected && 'is-selected',
      formatSelectorVisible && 'download-format-selector-visible',
      hideOnThreeColumnLayout && 'hidden-sm hidden-md',
      shouldShowSimilarContent && 'collapsed-card'
    );
  }, [
    isFocusOutlineVisible,
    className,
    isSelected,
    formatSelectorVisible,
    hideOnThreeColumnLayout,
    context,
  ]);

  const logHoverDone = useCallback(() => {
    if (hoverStartTime) {
      const hoverDuration = Date.now() - hoverStartTime;
      const playEnd = videoRef.current?.currentTime ?? 0;
      const newAccumulatedPlayMs = accumulatedPlayMs + playDurationMs();
      const timesLooped = Math.floor(
        newAccumulatedPlayMs / 1000 / stockItem.duration
      );
      const originArea = convertStockItemContextToOriginArea(context);

      let storyboardRequestId: string | undefined;
      let storyboardSceneRequestId: string | undefined;

      if (originArea === OriginArea.STORYBOARD) {
        storyboardRequestId = cookie.load(STORYBOARD_REQUEST_ID_COOKIE);
        storyboardSceneRequestId = stockDownloadContext.sceneRequestId;
      }

      const hoverEvent = StockItemHoverEventFactory.create(
        stockItem,
        hoverStartTime,
        playStartTime,
        playEnd,
        timesLooped,
        newAccumulatedPlayMs,
        Date.now(),
        originArea,
        storyboardRequestId,
        storyboardSceneRequestId
      );

      if (hoverDuration > 1000) {
        events.produce(hoverEvent, hoverEvent.user.visitorCookieId);
      }
    }
    // eslint-disable-next-line
  }, [
    hoverStartTime,
    accumulatedPlayMs,
    playDurationMs,
    stockItem,
    context,
    playStartTime,
    stockDownloadContext,
  ]);

  const logPrimaryClickAction = useCallback(() => {
    const stockItemId = stockItem.id;
    const position = stockItemCardContext && stockItemCardContext.position;
    const urlPath = '/api/video/stockItemClickEvent/download';
    const clickOrigin = getClickOriginArea();

    const beacon = sendBeacon(urlPath, stockItemId, position, clickOrigin);

    if (!beacon) {
      Logger.produceDownloadClickEvent(stockItem, position, clickOrigin);
    }
    logHoverDone();
  }, [stockItem, stockItemCardContext, getClickOriginArea, logHoverDone]);

  const logDetailsClickAction = useCallback(() => {
    const stockItemId = stockItem.id;
    const position = stockItemCardContext && stockItemCardContext.position;
    const urlPath = '/api/video/stockItemClickEvent/details';
    const clickOrigin = getClickOriginArea();

    const beacon = sendBeacon(urlPath, stockItemId, position, clickOrigin);

    if (!beacon) {
      Logger.produceDetailsClickEvent(stockItem, position, clickOrigin);
    }
    logHoverDone();
  }, [stockItem, stockItemCardContext, getClickOriginArea, logHoverDone]);

  const primaryActionUpdateSelectedStockItemOption = useCallback(() => {
    setIsHovering(false);
    dispatch(
      setSelectedStockItemOptions({
        id: stockItem.id,
        formatSelectorVisible: true,
        selectedFormatName: stockItemFormats[0].formatName,
        selectedFormatResolution: stockItemFormats[0].resolution,
      })
    );
  }, [dispatch, stockItem, stockItemFormats]);

  const primaryActionDownloadItem = useCallback(
    async (selectedFormat) => {
      const params = { ...stockDownloadContext };
      const originArea = convertStockItemContextToOriginArea(context);
      if (originArea) {
        params['origin_area'] = originArea;
      }
      Telemetry.increment('download.clicked.stockItemCard');
      const dataBridge = await SiteConstants.buildInstance().getDataBridge();
      await dataBridge.downloadItem(
        queryString.stringifyUrl(
          {
            url: selectedFormat.downloadUrl,
            query: params,
          },
          {
            skipNull: true,
          }
        ),
        queryString.stringifyUrl(
          {
            url: selectedFormat.downloadAjaxUrl,
            query: params,
          },
          {
            skipNull: true,
          }
        ),
        searchLogParameters,
        dispatch,
        primarySubscription || videoSubscription
      );
    },
    [
      stockDownloadContext,
      context,
      searchLogParameters,
      dispatch,
      primarySubscription,
      videoSubscription,
    ]
  );

  const defaultPrimaryClickAction = useCallback(
    async (event, clickedSelectedFormat = null) => {
      logPrimaryClickAction();
      if (!redirectToSignUpLink) {
        event.preventDefault();
        if (!formatSelectorVisible && stockItemFormats.length >= 2) {
          primaryActionUpdateSelectedStockItemOption();
        } else {
          const selectedFormat = clickedSelectedFormat
            ? clickedSelectedFormat
            : getSelectedFormat();
          await primaryActionDownloadItem(selectedFormat);
        }
      }
    },
    [
      logPrimaryClickAction,
      redirectToSignUpLink,
      formatSelectorVisible,
      stockItemFormats,
      primaryActionUpdateSelectedStockItemOption,
      getSelectedFormat,
      primaryActionDownloadItem,
    ]
  );

  const getPrimaryClickAction = useCallback(() => {
    if (isASubscriptionlessUser) {
      return async (_event) => {
        Telemetry.increment('preview.downloaded.stockItemCard');
        window.location.href = getPreviewUrl(
          stockItem.id,
          stockItem.contentClass
        );
      };
    }
    if (stockItem && !videoSubscription) {
      const visitorDownloadAttemptLink = stockItem.detailsUrl;
      return async (event) => {
        event.preventDefault();
        logPrimaryClickAction();
        window.location.href = visitorDownloadAttemptLink;
      };
    }
    return defaultPrimaryClickAction;
  }, [
    isASubscriptionlessUser,
    stockItem,
    videoSubscription,
    logPrimaryClickAction,
    defaultPrimaryClickAction,
  ]);

  const hideDownloadButtons = useCallback(
    (event) => {
      event.preventDefault();
      dispatch(
        setSelectedStockItemOptions({
          formatSelectorVisible: false,
        })
      );
      setIsHovering(true);
    },
    [dispatch]
  );

  const handleSelectedFormatChanged = useCallback(
    (selectedFormatName, selectedFormatResolution) => {
      dispatch(
        setSelectedStockItemOptions({
          id: stockItem.id,
          selectedFormatName,
          selectedFormatResolution,
        })
      );
    },
    [dispatch, stockItem]
  );

  const handleAIEditAction = useCallback(() => {
    if (shouldShowAIEditButton) {
      dispatch(openVideoEditorModal(stockItem));
      Telemetry.increment('ai.contentEdit.clicked.stockItemCard');
    }
  }, [dispatch, stockItem, shouldShowAIEditButton]);

  // Unsets the card hover state. Used for mouse enter/leave for the drawer.
  const unsetCardHover = useCallback(() => {
    setIsHovering(false);
    setPlayStartTime(null);
    setAccumulatedPlayMs((prev) => prev + playDurationMs());
    logHoverDone();
  }, [playDurationMs, logHoverDone]);

  const handleIsNotHovering = useCallback(
    (e) => {
      // Check if the blur is moving to a child element within the card
      const isMovingToChild =
        e?.relatedTarget instanceof Node &&
        containerRef.current?.contains(e.relatedTarget);

      if (!isDrawerHovering && !isMovingToChild) {
        unsetCardHover();
      }
    },
    [unsetCardHover, isDrawerHovering]
  );

  const isSimilarVideosDrawerOpen = () =>
    [
      drawerSelectedItemId === stockItem.id,
      drawerType === DrawerType.MORE_LIKE_THIS ||
        drawerType === DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS,
      drawerIsOpen,
    ].every(Boolean);

  const similarContentAction = useCallback(() => {
    if (isSimilarVideosDrawerOpen()) {
      dispatch(collapseMoreLikeThisDrawer());
      unsetCardHover();
      setIsDrawerHovering(false);
    } else {
      dispatch(
        expandMoreLikeThisDrawer({
          initialItemId: stockItem.id,
          drawerType: DrawerType.SAME_SHOOT_SAME_MODEL_SIMILAR_CLIPS,
        })
      );
    }
  }, [
    drawerIsOpen,
    drawerType,
    drawerSelectedItemId,
    stockItem,
    dispatch,
    stockItemCardContext,
  ]);

  useEffect(() => {
    if (isHovering && !isMobile) {
      play();
    } else if (!isHovering) {
      pause();
      reset(); // Reset to allow playing again on next hover
    }
  }, [isHovering, isMobile, play, pause, reset]);

  // Mouse enter/leave handlers
  const handleIsHovering = useCallback(
    (e) => {
      if (!formatSelectorVisible) {
        setIsHovering(true);
        setHoverStartTime(Date.now());
        // TODO: can this be removed?
        if (page) {
          updateSearchPage(page, stockItem.id)(dispatch);
        }
      }
    },
    [formatSelectorVisible, page, dispatch, stockItem]
  );

  // Need to manually unset the hover state in the drawer because it shares mouse enter/leave events with the card.
  const handleDrawerMouseEnter = useCallback(() => {
    setIsDrawerHovering(true);
    unsetCardHover();
  }, [unsetCardHover]);

  const handleDrawerMouseLeave = useCallback(() => {
    setIsDrawerHovering(false);
    unsetCardHover();
  }, [unsetCardHover]);

  const handleVideoStartedPlaying = useCallback(() => {
    setPlayStartTime((prev) => prev || Date.now());
  }, []);

  // Handle click action (for favorite, etc)
  const handleClickAction = useCallback(
    (e) => {
      if (!isMouseOverElement(e?.nativeEvent || e, containerRef.current)) {
        handleIsNotHovering(e);
      }
    },
    [handleIsNotHovering]
  );

  // Handle stock item click (for details)
  const handleStockItemClick = useCallback(
    (e) => {
      e.stopPropagation();
      const nodeName = e.target.nodeName;
      if (nodeName === 'VIDEO' && !isLoggerExecuted) {
        setIsLoggerExecuted(true);
        logDetailsClickAction();
      }
    },
    [isLoggerExecuted, logDetailsClickAction]
  );

  // Touch end for details link
  const handleOnTouchEnd = useCallback(() => {
    if (!isDragging) {
      window.location.href = stockItem.detailsUrl;
    }
  }, [isDragging, stockItem.detailsUrl]);

  // Renderers
  const renderStockItemFormatSelectorThumbnailExperiment = useCallback(
    () => (
      <VideoStockItemDownloader
        stockItemFormats={stockItemFormats}
        onClose={hideDownloadButtons}
        onSelect={async (event, format) => {
          handleSelectedFormatChanged(format.formatName, format.resolution);
          await getPrimaryClickAction()(event, format);
          hideDownloadButtons(event);
        }}
      />
    ),
    [
      stockItemFormats,
      hideDownloadButtons,
      handleSelectedFormatChanged,
      getPrimaryClickAction,
    ]
  );

  const renderStockItemFormatSelector = useCallback(
    () => (
      <VideoStockItemFormatSelector
        isVisible={formatSelectorVisible}
        stockItemFormats={stockItemFormats}
        selectedFormatName={selectedFormatName}
        selectedFormatResolution={selectedFormatResolution}
        selectedFormatChanged={handleSelectedFormatChanged}
        downloadPermissions={downloadPermissions}
      />
    ),
    [
      formatSelectorVisible,
      stockItemFormats,
      selectedFormatName,
      selectedFormatResolution,
      handleSelectedFormatChanged,
      downloadPermissions,
    ]
  );
  const renderVideo = useCallback(
    () => (
      <Video
        ref={videoRef}
        videoUrl={stockItem.previewUrl}
        poster={stockItem.thumbnailUrl}
        onStartedPlaying={handleVideoStartedPlaying}
        preload={false}
        mute={true}
        className="object-cover"
        style={
          videoStyles || {
            aspectRatio: stockItem.aspectRatio?.toString(),
            height: '100%',
          }
        }
      />
    ),
    [stockItem, handleVideoStartedPlaying, videoStyles]
  );

  const renderVideoWithDetailsLink = useCallback(() => {
    const src = stockDownloadContext.sceneRequestId
      ? `${stockItem.detailsUrl}?sceneRequestId=${stockDownloadContext.sceneRequestId}`
      : stockItem.detailsUrl;
    return (
      <a
        className="image-link"
        href={src}
        onTouchEnd={handleOnTouchEnd}
        aria-label={`Go to video details for ${stockItem.title}`}
        target={openDetailsInNewWindow ? '_blank' : '_self'}
        onClick={handleStockItemClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            handleStockItemClick(event);
          }
        }}
        tabIndex={0}
      >
        <div className="overflow-hidden md:rounded-3xl h-full w-full">
          {renderVideo()}
        </div>
      </a>
    );
  }, [
    stockDownloadContext,
    stockItem,
    handleOnTouchEnd,
    openDetailsInNewWindow,
    handleStockItemClick,
    renderVideo,
  ]);

  const primaryClickAction = getPrimaryClickAction();
  // Main render

  return (
    <div
      className={getContainerClassNames()}
      data-testid="video-stock-item-card"
      data-stock-id={stockItem.id}
      ref={containerRef}
      onMouseEnter={handleIsHovering}
      onFocus={handleIsHovering}
      onMouseLeave={handleIsNotHovering}
      onBlur={handleIsNotHovering}
      style={style}
      itemType="schema.org/VideoObject"
    >
      <div className="box thumbnail z-0 h-full">
        <div
          className={twMerge(
            'thumbnail-img-wrapper overflow-hidden rounded-lg w-full h-full relative',
            thumbnailClassNames
          )}
        >
          {formatSelectorVisible &&
            (useNewDownloadOverlay
              ? renderStockItemFormatSelectorThumbnailExperiment()
              : renderStockItemFormatSelector())}
          <StockItemIcons
            showNewFlag={shouldStockItemHaveNewFlag}
            className={`left-4 h-auto top-4 ${
              isHovering ? 'opacity-0' : 'opacity-100'
            }`}
            iconClassName={
              'bg-white px-1.5 py-1 leading-4 text-gray-900 text-sm'
            }
          />
          {draggableCard ? renderVideo() : renderVideoWithDetailsLink()}
          <LoadingIndicator show={isHovering && !isPlaying} />
        </div>
        <VideoCardOverlay
          duration={stockItem.duration}
          title={stockItem.title}
          shouldInvertControlsVisibility={shouldInvertControlsVisibility}
          isHovering={isHovering}
          className={overlayGradientClassNames}
        />
      </div>
      <VideoCardActionButtons
        isVertical={isVertical}
        context={context}
        isHovering={isHovering}
        shouldShowAIEditButton={
          shouldShowAIEditButton && shouldShowAIEditFeatures
        }
        handleAIEditAction={handleAIEditAction}
        containPoppers={containPoppers}
        stockItem={stockItem}
        stockItemFormats={stockItemFormats}
        favoriteButtonTelemetryAction={favoriteButtonTelemetryAction}
        openFoldersInNewWindow={openFoldersInNewWindow}
        handleClickAction={handleClickAction}
        shouldShowMakerButton={shouldShowMakerButton}
        makerButtonTelemetryAction={makerButtonTelemetryAction}
        relatedVideosButtonRef={relatedVideosButtonRef}
        similarContentAction={similarContentAction}
        shouldShowDownloadButton={shouldShowDownloadButton}
        downloadButtonTelemetryAction={downloadButtonTelemetryAction}
        onDownloadButtonClick={primaryClickAction}
        isContentEditPolling={isContentEditPolling}
        isSimilarVideosDrawerOpen={isSimilarVideosDrawerOpen()}
        drawerPortalContainer={drawerPortalContainer}
        stockItemCardContainer={containerRef?.current}
        onDrawerMouseEnter={handleDrawerMouseEnter}
        onDrawerMouseLeave={handleDrawerMouseLeave}
      />
    </div>
  );
};

export default VideoStockItemCard;
